import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { isBoard as checkIsBoard, isTrainer, isGuest, isMember, isAuditor } from '../utils/roleUtils';
import apiClient from '../utils/apiClient';
import packageJson from '../../package.json';
import BoardNotesPanel from './BoardNotesPanel';
import {
  Bars3Icon,
  XMarkIcon,
  CalendarIcon,
  UserGroupIcon,
  CurrencyEuroIcon,
  HomeIcon,
  UserCircleIcon,
  SunIcon,
  MoonIcon,
  ArrowRightOnRectangleIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  CogIcon,
  ShieldCheckIcon,
  FolderIcon,
  ChartBarIcon,
  CreditCardIcon,
  ShoppingCartIcon,
  ClipboardDocumentListIcon,
  AcademicCapIcon,
  SparklesIcon,
  CloudArrowDownIcon,
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import { FaPaypal, FaDog } from 'react-icons/fa';

// Create a typed icon component for PayPal
const PayPalIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaPaypal as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

// Create a typed icon component for Dog
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

// Page titles for header with icons (used for breadcrumb / large title)
// `storyIntro: true` → page provides its own Story-Hero-Intro, Layout-Title-Block ausgeblendet
const pageTitles: Record<string, { title: string; subtitle?: string; icon: string; storyIntro?: boolean }> = {
  '/dashboard': { title: 'Dashboard', subtitle: 'Übersicht', icon: 'home', storyIntro: true },
  '/calendar': { title: 'Kalender', subtitle: 'Mein Verein', icon: 'calendar', storyIntro: true },
  '/my-bookings': { title: 'Meine Buchungen', subtitle: 'Mein Verein', icon: 'calendar', storyIntro: true },
  '/my-drinks': { title: 'Meine Verzehrkosten', subtitle: 'Mein Verein', icon: 'cart', storyIntro: true },
  '/my-dogs': { title: 'Meine Hunde', subtitle: 'Mein Verein', icon: 'dog', storyIntro: true },
  '/my-guest-cards': { title: 'Meine Gästekarte', subtitle: 'Mein Verein', icon: 'card' },
  '/profile': { title: 'Mein Profil', subtitle: 'Mein Verein', icon: 'user', storyIntro: true },
  '/my-projects': { title: 'Vereinsprojekte', subtitle: 'Mein Verein', icon: 'folder', storyIntro: true },
  '/veranstaltungen': { title: 'Veranstaltungen', subtitle: 'Mein Verein', icon: 'ticket', storyIntro: true },
  '/admin/events': { title: 'Veranstaltungen', subtitle: 'Verwaltung', icon: 'ticket', storyIntro: true },
  '/members': { title: 'Mitglieder', subtitle: 'Verwaltung', icon: 'users', storyIntro: true },
  '/dog-management-beta': { title: 'Hunde', subtitle: 'Verwaltung', icon: 'dog', storyIntro: true },
  '/guest-cards': { title: 'Gästekarten', subtitle: 'Verwaltung', icon: 'card', storyIntro: true },
  '/verzehrkosten': { title: 'Verzehrkosten', subtitle: 'Verwaltung', icon: 'cart', storyIntro: true },
  '/admin/projects': { title: 'Projekte', subtitle: 'Verwaltung', icon: 'folder', storyIntro: true },
  '/admin/waitlist': { title: 'Warteliste', subtitle: 'Verwaltung', icon: 'clipboard', storyIntro: true },
  '/account-overview': { title: 'Finanzübersicht', subtitle: 'Finanzen', icon: 'chart', storyIntro: true },
  '/account-statements': { title: 'Kontoauszüge', subtitle: 'Finanzen', icon: 'document', storyIntro: true },
  '/budget-planning': { title: 'Budget-Planung', subtitle: 'Finanzen', icon: 'euro', storyIntro: true },
  '/paypal-payments': { title: 'PayPal-Zahlungen', subtitle: 'Finanzen', icon: 'paypal', storyIntro: true },
  '/kassenpruefung': { title: 'Kassenprüfung', subtitle: 'Finanzen', icon: 'shield', storyIntro: true },
  '/documents': { title: 'Dokumente', subtitle: 'Verwaltung', icon: 'folder', storyIntro: true },
  '/member-statistics': { title: 'Statistik', subtitle: 'Auswertung', icon: 'chart', storyIntro: true },
  '/admin/config': { title: 'Vereinseinstellungen', subtitle: 'Admin', icon: 'cog', storyIntro: true },
  '/admin/auditors': { title: 'Kassenprüfer', subtitle: 'Admin', icon: 'shield', storyIntro: true },
  '/admin/structure': { title: 'Vereinsstruktur', subtitle: 'Admin', icon: 'users', storyIntro: true },
  '/admin/backup-export': { title: 'Backup & Export', subtitle: 'Admin', icon: 'cloud', storyIntro: true },
  '/admin/paypal-setup': { title: 'PayPal-Setup', subtitle: 'Admin', icon: 'paypal', storyIntro: true },
  '/admin/privacy': { title: 'DSGVO', subtitle: 'Admin', icon: 'shield', storyIntro: true },
  '/admin/components-demo': { title: 'Design System', subtitle: 'Admin', icon: 'sparkles', storyIntro: true },
};

// V2 iOS Large Title — Eyebrow color mapping based on subtitle
const getEyebrowColorClass = (subtitle?: string): string => {
  if (!subtitle) return 'text-gray-500 dark:text-gray-400';
  const s = subtitle.toLowerCase();
  if (s.includes('verwalt') || s.includes('admin')) return 'text-warning-600 dark:text-warning-400';
  if (s.includes('finanz') || s.includes('zahlung') || s.includes('paypal')) return 'text-info-600 dark:text-info-400';
  if (s.includes('demo')) return 'text-fuchsia-600 dark:text-fuchsia-400';
  if (s.includes('archiv') || s.includes('datei')) return 'text-info-600 dark:text-info-400';
  if (s.includes('auswert')) return 'text-info-600 dark:text-info-400';
  return 'text-primary-600 dark:text-primary-400';
};

// Icon component for header (mobile + breadcrumb)
const MobileHeaderIcon: React.FC<{ icon: string }> = ({ icon }) => {
  const iconClass = "h-5 w-5 text-primary-500";
  switch (icon) {
    case 'home': return <HomeIcon className={iconClass} />;
    case 'calendar': return <CalendarIcon className={iconClass} />;
    case 'cart': return <ShoppingCartIcon className={iconClass} />;
    case 'dog': return <DogIcon className={iconClass} />;
    case 'card': return <CreditCardIcon className={iconClass} />;
    case 'user': return <UserCircleIcon className={iconClass} />;
    case 'folder': return <FolderIcon className={iconClass} />;
    case 'users': return <UserGroupIcon className={iconClass} />;
    case 'chart': return <ChartBarIcon className={iconClass} />;
    case 'document': return <DocumentTextIcon className={iconClass} />;
    case 'euro': return <CurrencyEuroIcon className={iconClass} />;
    case 'paypal': return <PayPalIcon className={iconClass} />;
    case 'cog': return <CogIcon className={iconClass} />;
    case 'shield': return <ShieldCheckIcon className={iconClass} />;
    case 'sparkles': return <SparklesIcon className={iconClass} />;
    case 'ticket': return <TicketIcon className={iconClass} />;
    case 'clipboard': return <ClipboardDocumentListIcon className={iconClass} />;
    case 'cloud': return <CloudArrowDownIcon className={iconClass} />;
    default: return <HomeIcon className={iconClass} />;
  }
};

// V2-A: NavItem — wiederverwendbare Sidebar-Navigation mit Section-Color
type SectionColor = 'primary' | 'warning' | 'info';

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  sectionColor: SectionColor;
  onClick?: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, label, sectionColor, onClick }) => {
  const colorMap: Record<SectionColor, { bg: string; text: string; border: string }> = {
    primary: {
      bg: 'bg-primary-600/15 dark:bg-primary-600/20',
      text: 'text-primary-700 dark:text-primary-400',
      border: 'border-l-primary-600',
    },
    warning: {
      bg: 'bg-warning-600/15 dark:bg-warning-600/20',
      text: 'text-warning-700 dark:text-warning-400',
      border: 'border-l-warning-600',
    },
    info: {
      bg: 'bg-info-600/15 dark:bg-info-600/20',
      text: 'text-info-700 dark:text-info-400',
      border: 'border-l-info-600',
    },
  };
  const c = colorMap[sectionColor];
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center pl-[13px] pr-4 py-2 rounded-lg border-l-[3px] transition-colors text-sm ${
          isActive
            ? `${c.bg} ${c.text} ${c.border} font-semibold`
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 border-l-transparent'
        }`
      }
    >
      <span className="h-5 w-5 mr-3 flex-shrink-0 flex items-center justify-center">{icon}</span>
      <span className="truncate">{label}</span>
    </NavLink>
  );
};

// V2-A: NavSection-Header (small, uppercase, color-coded)
const NavSection: React.FC<{ label: string; color: SectionColor }> = ({ label, color }) => {
  const colorMap: Record<SectionColor, string> = {
    primary: 'text-primary-600 dark:text-primary-400',
    warning: 'text-warning-600 dark:text-warning-400',
    info: 'text-info-600 dark:text-info-400',
  };
  return (
    <h3 className={`px-3 pt-3.5 pb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] ${colorMap[color]}`}>
      {label}
    </h3>
  );
};

// Helper: Initials from user (Vorname + Nachname)
// Voller Anzeigename: kombiniert vorname + name/nachname robust
const getDisplayName = (user: any): string => {
  if (!user) return '';
  const v = (user.vorname || '').trim();
  // user.name kann "Vorname Nachname" sein (API-Antwort) oder nur Nachname (lokaler State)
  const nameField = (user.name || user.nachname || '').trim();
  if (v && nameField && !nameField.includes(' ')) return `${v} ${nameField}`;
  if (nameField) return nameField; // schon vollständig oder allein verfügbar
  return v || '';
};

const getInitials = (user: any): string => {
  if (!user) return '?';
  const full = getDisplayName(user);
  if (!full) return '?';
  const parts = full.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
};

// Helper: Role badges for User-Card
const RoleBadges: React.FC<{ user: any }> = ({ user }) => {
  if (!user) return null;
  const badges: Array<{ label: string; cls: string }> = [];
  if (checkIsBoard(user)) {
    badges.push({
      label: 'VORSTAND',
      cls: 'bg-primary-600/20 text-primary-700 dark:text-primary-300 border-primary-600/40',
    });
  }
  if (isTrainer(user)) {
    badges.push({
      label: 'TRAINER',
      cls: 'bg-info-600/20 text-info-700 dark:text-info-200 border-info-600/40',
    });
  }
  if (isAuditor(user)) {
    badges.push({
      label: 'KASSENPRÜFER',
      cls: 'bg-warning-600/20 text-warning-700 dark:text-warning-300 border-warning-600/40',
    });
  }
  if (isGuest(user)) {
    badges.push({
      label: 'GAST',
      cls: 'bg-warning-500/20 text-warning-700 dark:text-warning-300 border-warning-500/40',
    });
  } else if (isMember(user) && !checkIsBoard(user) && !isTrainer(user)) {
    // Mitglied nur anzeigen, wenn keine andere Rolle dominiert — sonst zu viele Pills
    badges.push({
      label: 'MITGLIED',
      cls: 'bg-gray-200 dark:bg-white/5 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-white/10',
    });
  }
  return (
    <div className="flex flex-wrap gap-1 mt-0.5">
      {badges.map((b) => (
        <span
          key={b.label}
          className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded-md border ${b.cls}`}
        >
          {b.label}
        </span>
      ))}
    </div>
  );
};

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasActiveProjects, setHasActiveProjects] = useState(false);
  const [hasActiveEvents, setHasActiveEvents] = useState(false);
  const [hasOpenAudits, setHasOpenAudits] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [unreadNotesCount, setUnreadNotesCount] = useState(0);
  const [backendVersion, setBackendVersion] = useState<string>('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout, darkMode, toggleDarkMode } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const clubName = localStorage.getItem('club_name') || 'Hundesportverein';

  // Get current page title
  const currentPage = pageTitles[location.pathname] || { title: clubName, icon: 'home' };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  // Fetch backend version
  useEffect(() => {
    apiClient.get('/api/version')
      .then(res => { if (res.data?.version) setBackendVersion(res.data.version); })
      .catch(() => {});
  }, []);

  // Check if there are active projects for members
  useEffect(() => {
    const checkActiveProjects = async () => {
      try {
        const response = await apiClient.get('/api/projects');
        if (response.data.success) {
          const activeProjects = response.data.projects.filter((p: any) => p.status === 'active');
          setHasActiveProjects(activeProjects.length > 0);
        }
      } catch (error) {
        setHasActiveProjects(false);
      }
    };
    checkActiveProjects();
  }, []);

  // Check if there are active events for members
  useEffect(() => {
    const checkActiveEvents = async () => {
      try {
        const response = await apiClient.get('/api/veranstaltungen/admin/has-active');
        if (response.data.success) {
          setHasActiveEvents(response.data.hasActive);
        }
      } catch (error) {
        setHasActiveEvents(false);
      }
    };
    checkActiveEvents();
  }, []);

  // Check if auditor has open audits
  useEffect(() => {
    const checkOpenAudits = async () => {
      if (!isAuditor(user)) {
        setHasOpenAudits(false);
        return;
      }
      try {
        const response = await apiClient.get('/api/audit/has-open');
        if (response.data.success) {
          setHasOpenAudits(response.data.hasOpen);
        }
      } catch (error) {
        setHasOpenAudits(false);
      }
    };
    checkOpenAudits();
  }, [user]);

  // Board Notes: Ungelesene Nachrichten polling
  const fetchUnreadCount = useCallback(async () => {
    if (!checkIsBoard(user)) return;
    try {
      const res = await apiClient.get('/api/board-notes/unread-count');
      setUnreadNotesCount(res.data?.count || 0);
    } catch {}
  }, [user]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Close user menu on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocClick = () => setUserMenuOpen(false);
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [userMenuOpen]);

  const isBoardMember = checkIsBoard(user);
  const isTrainerUser = isTrainer(user);
  const isGuestUser = isGuest(user);
  const isMemberUser = isMember(user);
  const isAuditorUser = isAuditor(user);

  // Determine which sections to show based on role
  const showDashboard = isBoardMember;
  const showMemberSection = isBoardMember;
  const showFinanceSection = isBoardMember;
  const showAdminSection = isBoardMember;
  const showAuditSection = isBoardMember || (isAuditorUser && hasOpenAudits);
  const showGuestCards = isGuestUser;
  const showMyBookings = isBoardMember || isTrainerUser || isMemberUser;
  const showDrinks = (isBoardMember || isTrainerUser || isMemberUser) && !isGuestUser;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-background-dark">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-white dark:bg-surface-dark border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Sidebar Header — Logo + Vereinsname */}
          <div className="flex h-16 items-center justify-between px-5 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <img
                src="/HSV_Logo.jpg"
                alt="HSV Berlin-Adlershof Logo"
                className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-bold text-gray-900 dark:text-white leading-tight truncate">
                  Hundesportverein
                </div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate leading-tight">
                  Berlin-Adlershof e.V.
                </div>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 ml-2"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation — relative wrapper für unteren Fade-Indikator */}
          <div className="relative flex-1 min-h-0">
            <nav
              className="absolute inset-0 px-3 pt-2 pb-6 overflow-y-auto sidebar-scroll"
              style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(148,163,184,0.4) transparent' }}
            >
            {/* MEIN VEREIN (zuerst) */}
            <NavSection label="Mein Verein" color="primary" />
            <div className="space-y-0.5">
              {showDashboard && (
                <NavItem
                  to="/dashboard"
                  icon={<HomeIcon className="h-5 w-5" />}
                  label="Dashboard"
                  sectionColor="primary"
                  onClick={closeSidebar}
                />
              )}
              <NavItem
                to="/calendar"
                icon={<CalendarIcon className="h-5 w-5" />}
                label="Kalender"
                sectionColor="primary"
                onClick={closeSidebar}
              />
              {showMyBookings && (
                <NavItem
                  to="/my-bookings"
                  icon={<CalendarIcon className="h-5 w-5" />}
                  label="Meine Buchungen"
                  sectionColor="primary"
                  onClick={closeSidebar}
                />
              )}
              {showDrinks && (
                <NavItem
                  to="/my-drinks"
                  icon={<ShoppingCartIcon className="h-5 w-5" />}
                  label="Meine Verzehrkosten"
                  sectionColor="primary"
                  onClick={closeSidebar}
                />
              )}
              <NavItem
                to="/my-dogs"
                icon={<DogIcon className="h-5 w-5" />}
                label="Meine Hunde"
                sectionColor="primary"
                onClick={closeSidebar}
              />
              {showGuestCards && (
                <NavItem
                  to="/my-guest-cards"
                  icon={<CreditCardIcon className="h-5 w-5" />}
                  label="Meine Gästekarte"
                  sectionColor="primary"
                  onClick={closeSidebar}
                />
              )}
              <NavItem
                to="/profile"
                icon={<UserCircleIcon className="h-5 w-5" />}
                label="Mein Profil"
                sectionColor="primary"
                onClick={closeSidebar}
              />
              {hasActiveProjects && !isGuestUser && (
                <NavItem
                  to="/my-projects"
                  icon={<FolderIcon className="h-5 w-5" />}
                  label="Vereinsprojekte"
                  sectionColor="primary"
                  onClick={closeSidebar}
                />
              )}
              {hasActiveEvents && !isGuestUser && (
                <NavItem
                  to="/veranstaltungen"
                  icon={<TicketIcon className="h-5 w-5" />}
                  label="Veranstaltungen"
                  sectionColor="primary"
                  onClick={closeSidebar}
                />
              )}
            </div>

            {/* VERWALTUNG (darunter) */}
            {showMemberSection && (
              <>
                <NavSection label="Verwaltung" color="warning" />
                <div className="space-y-0.5">
                  <NavItem
                    to="/members"
                    icon={<UserGroupIcon className="h-5 w-5" />}
                    label="Mitglieder"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                  <NavItem
                    to="/dog-management-beta"
                    icon={<DogIcon className="h-5 w-5" />}
                    label="Hunde"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                  <NavItem
                    to="/guest-cards"
                    icon={<CreditCardIcon className="h-5 w-5" />}
                    label="Gästekarten"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                  <NavItem
                    to="/verzehrkosten"
                    icon={<ShoppingCartIcon className="h-5 w-5" />}
                    label="Verzehrkosten"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                  <NavItem
                    to="/admin/waitlist"
                    icon={<ClipboardDocumentListIcon className="h-5 w-5" />}
                    label="Warteliste"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                  <NavItem
                    to="/admin/projects"
                    icon={<FolderIcon className="h-5 w-5" />}
                    label="Projektverwaltung"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                  <NavItem
                    to="/admin/events"
                    icon={<TicketIcon className="h-5 w-5" />}
                    label="Veranstaltungen"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                  <NavItem
                    to="/documents"
                    icon={<FolderIcon className="h-5 w-5" />}
                    label="Dokumente"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                </div>
              </>
            )}

            {/* FINANZEN */}
            {(showFinanceSection || showAuditSection) && (
              <>
                <NavSection label="Finanzen" color="info" />
                <div className="space-y-0.5">
                  {showFinanceSection && (
                    <>
                      <NavItem
                        to="/account-overview"
                        icon={<ChartBarIcon className="h-5 w-5" />}
                        label="Finanzübersicht"
                        sectionColor="info"
                        onClick={closeSidebar}
                      />
                      <NavItem
                        to="/account-statements"
                        icon={<DocumentTextIcon className="h-5 w-5" />}
                        label="Kontoauszüge"
                        sectionColor="info"
                        onClick={closeSidebar}
                      />
                      <NavItem
                        to="/budget-planning"
                        icon={<CurrencyEuroIcon className="h-5 w-5" />}
                        label="Budget-Planung"
                        sectionColor="info"
                        onClick={closeSidebar}
                      />
                      <NavItem
                        to="/paypal-payments"
                        icon={<PayPalIcon className="h-5 w-5" />}
                        label="PayPal-Zahlungen"
                        sectionColor="info"
                        onClick={closeSidebar}
                      />
                    </>
                  )}
                  {showAuditSection && (
                    <NavItem
                      to="/kassenpruefung"
                      icon={<ShieldCheckIcon className="h-5 w-5" />}
                      label="Kassenprüfung"
                      sectionColor="info"
                      onClick={closeSidebar}
                    />
                  )}
                </div>
              </>
            )}

            {/* ADMIN */}
            {showAdminSection && (
              <>
                <NavSection label="Admin" color="warning" />
                <div className="space-y-0.5">
                  <NavItem
                    to="/admin/config"
                    icon={<CogIcon className="h-5 w-5" />}
                    label="Vereinseinstellungen"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                  <NavItem
                    to="/admin/auditors"
                    icon={<ShieldCheckIcon className="h-5 w-5" />}
                    label="Kassenprüfer"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                  <NavItem
                    to="/admin/structure"
                    icon={<BuildingOfficeIcon className="h-5 w-5" />}
                    label="Vereinsstruktur"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                  <NavItem
                    to="/admin/backup-export"
                    icon={<CloudArrowDownIcon className="h-5 w-5" />}
                    label="Backup & Export"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                  <NavItem
                    to="/admin/paypal-setup"
                    icon={<AcademicCapIcon className="h-5 w-5" />}
                    label="PayPal-Setup"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                  <NavItem
                    to="/admin/components-demo"
                    icon={<SparklesIcon className="h-5 w-5" />}
                    label="Design System"
                    sectionColor="warning"
                    onClick={closeSidebar}
                  />
                </div>
              </>
            )}

            {/* AUSWERTUNG */}
            {showAdminSection && (
              <>
                <NavSection label="Auswertung" color="info" />
                <div className="space-y-0.5">
                  <NavItem
                    to="/member-statistics"
                    icon={<ChartBarIcon className="h-5 w-5" />}
                    label="Statistik"
                    sectionColor="info"
                    onClick={closeSidebar}
                  />
                </div>
              </>
            )}
            </nav>
            {/* Fade-Indikator: zeigt subtil dass mehr Inhalt unten ist */}
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white dark:from-surface-dark to-transparent" />
          </div>

          {/* Sidebar Footer — User-Card mit horizontaler Action-Bar */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-2.5 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
            <div className="rounded-2xl bg-gradient-to-br from-gray-50 to-white dark:from-white/[0.05] dark:to-white/[0.02] border border-gray-200/80 dark:border-white/[0.06] overflow-hidden shadow-sm">
              {/* Top: Profil-Row */}
              <button
                onClick={() => { navigate('/profile'); closeSidebar(); }}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 hover:bg-white/80 dark:hover:bg-white/[0.04] transition-colors text-left group"
                title="Mein Profil"
              >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 grid place-items-center text-white font-bold text-sm shadow-sm flex-shrink-0">
                  {getInitials(user)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold text-gray-900 dark:text-white truncate leading-tight group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-colors">
                    {getDisplayName(user) || 'Mein Profil'}
                  </div>
                  <RoleBadges user={user} />
                </div>
                <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-600 flex-shrink-0 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" />
              </button>
              {/* Divider */}
              <div className="h-px bg-gray-200/70 dark:bg-white/[0.05]" />
              {/* Bottom: Action-Bar */}
              <div className="grid grid-cols-2">
                <button
                  onClick={toggleDarkMode}
                  className="flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-semibold text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-white/[0.05] hover:text-gray-900 dark:hover:text-white transition-colors"
                  title={darkMode ? 'Light Mode aktivieren' : 'Dark Mode aktivieren'}
                >
                  {darkMode ? <SunIcon className="h-3.5 w-3.5" /> : <MoonIcon className="h-3.5 w-3.5" />}
                  <span>{darkMode ? 'Light' : 'Dark'}</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-semibold text-gray-600 dark:text-gray-400 hover:bg-error-50 dark:hover:bg-error-900/20 hover:text-error-700 dark:hover:text-error-300 transition-colors border-l border-gray-200/70 dark:border-white/[0.05]"
                  title="Abmelden"
                >
                  <ArrowRightOnRectangleIcon className="h-3.5 w-3.5" />
                  <span>Abmelden</span>
                </button>
              </div>
            </div>
            <div className="px-2 pt-1.5 text-[9px] text-gray-400 dark:text-gray-600 font-mono leading-tight tracking-tight whitespace-nowrap overflow-hidden text-ellipsis">
              v{packageJson.version}{backendVersion ? ` · BE v${backendVersion}` : ''} · © {new Date().getFullYear()}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:pl-72 min-h-screen flex flex-col">
        {/* Top Bar — Mobile: kompakte Toolbar; Desktop: Breadcrumb + Aktionen */}
        <header className="sticky top-0 z-30 flex h-12 lg:h-14 items-center bg-white/95 dark:bg-surface-dark/95 backdrop-blur-md px-4 sm:px-6 lg:px-8 lg:border-b lg:border-gray-200 lg:dark:border-gray-700">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 -ml-2 p-2"
            title="Menü öffnen"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Desktop: Breadcrumb [Sektion] › [Page] */}
          <div className="hidden lg:flex items-center gap-1.5 text-[12px]">
            {currentPage.subtitle && (
              <>
                <span className="text-gray-500 dark:text-gray-500">{currentPage.subtitle}</span>
                <ChevronRightIcon className="h-3 w-3 text-gray-400 dark:text-gray-600" />
              </>
            )}
            <span className="text-gray-700 dark:text-gray-200 font-medium">{currentPage.title}</span>
          </div>

          {/* Mobile: Title bleibt absichtlich leer — V2 zeigt Title als Large-Block im Content */}

          <div className="flex-1" />

          {/* Right: Board Notes Button + Avatar */}
          {isBoardMember && (
            <button
              onClick={() => setNotesOpen(true)}
              className="relative w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
              title="Vorstand-Nachrichten"
            >
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              {unreadNotesCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-error-500 text-white text-[10px] font-bold">
                  {unreadNotesCount > 99 ? '99+' : unreadNotesCount}
                </span>
              )}
            </button>
          )}

        </header>

        {/* V2 Title Block — sichtbar auf Mobile UND Desktop.
            Auf Story-Intro-Pages ausgeblendet (Page liefert eigene Headline).
            Action-Slot bleibt jedoch erhalten. */}
        {currentPage.storyIntro ? (
          <div className="px-4 pt-2 lg:px-8 lg:pt-4 flex justify-end">
            <div id="layout-action-slot" className="flex-shrink-0" />
          </div>
        ) : (
          <div className="px-4 pt-2 pb-3 lg:px-8 lg:pt-6 lg:pb-6">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                {currentPage.subtitle && (
                  <div className={`inline-flex items-center gap-1.5 text-[11px] lg:text-[11px] uppercase tracking-[0.18em] font-semibold ${getEyebrowColorClass(currentPage.subtitle)}`}>
                    <MobileHeaderIcon icon={currentPage.icon} />
                    <span>{currentPage.subtitle}</span>
                  </div>
                )}
                <h1 className="text-[34px] lg:text-[40px] font-extrabold leading-[1.05] text-gray-900 dark:text-white mt-1 break-words tracking-[-0.015em]">
                  {currentPage.title}
                </h1>
              </div>
              {/* Action-Slot — Pages bringen ihre eigenen Action-Bars */}
              <div id="layout-action-slot" className="flex-shrink-0" />
            </div>
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 px-2 py-3 sm:p-6 lg:px-8 lg:pb-12 lg:pt-0">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
              <div className="flex items-center gap-3 text-xs">
                <span className="text-gray-600 dark:text-gray-400">
                  © {new Date().getFullYear()} {clubName}
                </span>
                <span className="hidden sm:inline text-gray-400 dark:text-gray-600">•</span>
                <span className="font-mono text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 sm:px-2 sm:py-0.5 sm:bg-gray-100 sm:dark:bg-gray-800 sm:rounded sm:text-gray-500 sm:dark:text-gray-400">
                  v{packageJson.version}{backendVersion ? ` / BE v${backendVersion}` : ''}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-gray-500 dark:text-gray-400">
                  Bereitgestellt von{' '}
                  <a
                    href="https://mein-hundesportverein.de"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors font-medium"
                  >
                    mein-hundesportverein.de
                  </a>
                </span>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Board Notes Panel */}
      {isBoardMember && (
        <BoardNotesPanel
          isOpen={notesOpen}
          onClose={() => setNotesOpen(false)}
          pageContext={currentPage.title}
          onUnreadCountChange={setUnreadNotesCount}
        />
      )}
    </div>
  );
};

export default Layout;
